import {useMemo} from 'react';
import type {Sorting} from '@igp/shared/api';
import {useAuthUser} from '@igp/shared/auth';
import {type MapResult, useAutoMapper} from '@igp/shared/auto-mapper';
import type {RawItem, UseListViewEntityProps, UseListViewEntityResult} from '@igp/shared/redux/view';
import {extendedViewCleanDelay, useViewInit} from '@igp/shared/redux/view';
import {useColumnsSettings} from '@igp/shared/user-settings';
import {momentToTimestampSeconds} from '@igp/shared/utils';
import {EntityType} from '@models';

import {firstAvailableDate} from './constants';
import {countryLevelPartnerReportMappingKeys} from './mapper';
import type {
    CountryLevelPartnerReportColumnSettings,
    CountryLevelPartnerReportColumnSettingsKeys,
    CountryLevelPartnerReportQueryFilterKeys,
    CountryLevelPartnerReportQueryKeys,
    CountryLevelPartnerReportSortKeys,
    CountryLevelPartnerReportViewModel,
    CountryLevelPartnerReportViewModelKeys,
    CountryLevelReport,
} from './types';

type UseCountryLevelPartnerReportProps = Omit<
    UseListViewEntityProps<CountryLevelPartnerReportQueryFilterKeys, CountryLevelPartnerReportViewModelKeys>,
    'schemaMapper' | 'isCancelable' | 'realtimeMode' | 'triggers' | 'ids'
> & {
    hasSummary?: boolean;
};

type UseCountryLevelPartnerReportResult = UseListViewEntityResult<
    CountryLevelPartnerReportViewModel,
    CountryLevelPartnerReportViewModelKeys
> & {
    summary: CountryLevelPartnerReportViewModel;
    summaryRaw: RawItem;
};

export function useCountryLevelPartnerReport({
    viewType,
    fields,
    defaultFilters,
    defaultSorting,
    defaultPaging,
    syncWithUrl,
    validateFilter,
    hasSummary,
    cleanDelay = extendedViewCleanDelay,
    displayName,
}: UseCountryLevelPartnerReportProps): UseCountryLevelPartnerReportResult {
    const mapper = useAutoMapper();
    const queryFields = useMemo(() => getQueryFields(fields), [fields]);
    const mappedSorting = useMemo(() => mapSorting(defaultSorting), [defaultSorting]);

    const {
        items: serverItems,
        totalCount,
        viewEntity,
        getSearchFilter,
        handleSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    } = useViewInit<
        EntityType,
        CountryLevelReport,
        CountryLevelPartnerReportQueryFilterKeys,
        CountryLevelPartnerReportQueryKeys,
        CountryLevelPartnerReportSortKeys
    >({
        viewType,
        entity: {entity: EntityType.CountryLevelPartnerReport, fields: queryFields},
        cleanDelay,
        defaultFilters,
        hiddenFilters: [{key: 'date_min', value: momentToTimestampSeconds(firstAvailableDate)}],
        defaultPaging,
        defaultSorting: mappedSorting,
        syncWithUrl,
        validateFilter,
        displayName,
    });

    const items: MapResult<CountryLevelPartnerReportViewModel>[] = useMemo(() => {
        return serverItems
            ?.filter(i => !i.is_summary)
            ?.map(i =>
                mapper.tryMap<CountryLevelReport, CountryLevelPartnerReportViewModel>(
                    i,
                    countryLevelPartnerReportMappingKeys.model,
                    countryLevelPartnerReportMappingKeys.viewModel
                )
            );
    }, [serverItems]);

    const summary: MapResult<CountryLevelPartnerReportViewModel> = useMemo(() => {
        const summaryModel = serverItems?.find(i => i.is_summary);
        return summaryModel
            ? mapper.tryMap<CountryLevelReport, CountryLevelPartnerReportViewModel>(
                  summaryModel,
                  countryLevelPartnerReportMappingKeys.model,
                  countryLevelPartnerReportMappingKeys.viewModel
              )
            : null;
    }, [serverItems]);

    const searchFilter = useMemo(() => {
        return getSearchFilter(i =>
            mapper.map<CountryLevelPartnerReportSortKeys, CountryLevelPartnerReportViewModelKeys>(
                i,
                countryLevelPartnerReportMappingKeys.sortKeys,
                countryLevelPartnerReportMappingKeys.viewModelsKeys
            )
        );
    }, [getSearchFilter]);

    function getQueryFields(vmKeys: CountryLevelPartnerReportViewModelKeys[]) {
        const queryItemsFields =
            vmKeys.map(f =>
                mapper.map<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportQueryKeys>(
                    f,
                    countryLevelPartnerReportMappingKeys.viewModelsKeys,
                    countryLevelPartnerReportMappingKeys.queryItemsKeys
                )
            ) ?? [];
        const querySummaryFields = hasSummary
            ? vmKeys.map(f =>
                  mapper.map<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportQueryKeys>(
                      f,
                      countryLevelPartnerReportMappingKeys.viewModelsKeys,
                      countryLevelPartnerReportMappingKeys.querySummaryKeys
                  )
              )
            : [];

        return [...queryItemsFields, ...querySummaryFields].filter(f => f);
    }

    function mapSorting(sorting: Sorting<CountryLevelPartnerReportViewModelKeys>[]): Sorting<CountryLevelPartnerReportSortKeys>[] {
        return (
            sorting?.map(i => ({
                field: mapper.map<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportSortKeys>(
                    i.field,
                    countryLevelPartnerReportMappingKeys.viewModelsKeys,
                    countryLevelPartnerReportMappingKeys.sortKeys
                ),
                sort: i.sort,
            })) ?? []
        );
    }

    function handleVmSortChange(sorting: Sorting<CountryLevelPartnerReportViewModelKeys>[]): void {
        handleSortChange(mapSorting(sorting));
    }

    return {
        items: items?.map(i => i.model),
        rawItems: items?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        summary: summary?.model,
        summaryRaw: {isValid: summary?.isModelValid, raw: summary?.rawModelString},
        totalCount,
        searchFilter,
        filterString: viewEntity?.filter,
        handleSortChange: handleVmSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    };
}

type UseCountryLevelPartnerReportSettingsProps = {
    gridId: string;
    defaultColumns: CountryLevelPartnerReportViewModelKeys[];
    pinnedColumns?: CountryLevelPartnerReportViewModelKeys[];
};

type UseCountryLevelPartnerReportSettingsResult = {
    visibleColumns?: CountryLevelPartnerReportViewModelKeys[];
    onVisibleColumnsChange: (value: CountryLevelPartnerReportViewModelKeys[]) => void;
};

export function useCountryLevelPartnerReportSettings({
    gridId,
    defaultColumns,
    pinnedColumns,
}: UseCountryLevelPartnerReportSettingsProps): UseCountryLevelPartnerReportSettingsResult {
    const {sub} = useAuthUser();
    const mapper = useAutoMapper();
    const {visible, onVisibleColumnsChange} = useColumnsSettings<CountryLevelPartnerReportColumnSettings>({
        userId: sub,
        settingId: gridId,
        defaultColumns: mapViewModelKeysToSettingsKeys(defaultColumns),
        pinnedColumns: mapViewModelKeysToSettingsKeys(pinnedColumns),
    });

    function mapViewModelKeysToSettingsKeys(keys: CountryLevelPartnerReportViewModelKeys[]): CountryLevelPartnerReportColumnSettingsKeys[] {
        return [
            ...new Set(
                keys?.map(k =>
                    mapper.map<CountryLevelPartnerReportViewModelKeys, CountryLevelPartnerReportColumnSettingsKeys>(
                        k,
                        countryLevelPartnerReportMappingKeys.viewModelsKeys,
                        countryLevelPartnerReportMappingKeys.settingKeys
                    )
                ) ?? []
            ),
        ];
    }

    function mapSettingsKeysToViewModelKeys(keys: CountryLevelPartnerReportColumnSettingsKeys[]): CountryLevelPartnerReportViewModelKeys[] {
        return [
            ...new Set(
                keys
                    ?.map(k =>
                        mapper.map<CountryLevelPartnerReportColumnSettingsKeys, CountryLevelPartnerReportViewModelKeys>(
                            k,
                            countryLevelPartnerReportMappingKeys.settingKeys,
                            countryLevelPartnerReportMappingKeys.viewModelsKeys
                        )
                    )
                    ?.filter(f => f) ?? []
            ),
        ];
    }

    function handleColumnSettingsChange(keys: CountryLevelPartnerReportViewModelKeys[]): void {
        const columns = mapViewModelKeysToSettingsKeys(keys);
        onVisibleColumnsChange(columns);
    }

    return {
        visibleColumns: mapSettingsKeysToViewModelKeys(visible as CountryLevelPartnerReportColumnSettingsKeys[]),
        onVisibleColumnsChange: handleColumnSettingsChange,
    };
}
